import Phaser from 'phaser';
import type { Meld, MeldPos, Tile } from '@rummikub/engine';
import { validateMeld } from '@rummikub/engine';
import { useStore } from '../store';
import { send } from '../net';
import { play as playSfx } from '../audio';
import { ensureTileTexture, TILE_H, TILE_W } from './textures';

const RACK_PAD = 12;
const RACK_GAP = 6;
const MAX_BOARD_COLS = 16;       // 上限（單一 meld 最長 13，加緩衝）
const MAX_BOARD_ROWS = 12;       // 上限
const BASE_BOARD_COLS = 8;       // 初始顯示（小 → tile 大）
const BASE_BOARD_ROWS = 4;
const RACK_COLS = 10;
const RACK_MAX_ROWS = 3;
const RACK_TOTAL_SLOTS = RACK_COLS * RACK_MAX_ROWS;
const LONG_PRESS_FULL_MS = 600;     // 長按 0.6s → 抓整段
const LONG_PRESS_MOVE_PX = 8;

interface TileSprite extends Phaser.GameObjects.Container {
  __tile: Tile;
  __home?: { x: number; y: number };
  __shadow?: Phaser.GameObjects.Image;
}

export class GameScene extends Phaser.Scene {
  private tileMap = new Map<string, TileSprite>();
  private meldBorders = new Map<number, Phaser.GameObjects.Graphics>();
  private ghostPreview?: Phaser.GameObjects.Image;
  private rackBg!: Phaser.GameObjects.Graphics;
  private tableDecor!: Phaser.GameObjects.Graphics;
  private storeUnsub: (() => void) | null = null;
  private draftBoard: Meld[] = [];
  private draftRack: Tile[] = [];
  private meldPositions: MeldPos[] = [];
  private hoverCell: { row: number; col: number } | null = null;
  private tileScale = 1;
  private tileW = TILE_W;
  private tileH = TILE_H;
  private rackRows = 1;
  private W = 1280;
  private H = 800;
  /** 牌庫位置（右上方靠近牌堆 HUD） */
  private get deckPos() {
    return {
      x: this.W - this.rightSafe / 2 - 10,
      y: this.boardTop + 30,
    };
  }
  /** 上一輪 layoutAll 的手牌 id 集合，用來偵測新抽到的牌 */
  private prevRackIds = new Set<string>();
  /** 上一輪每個玩家的 rackCount，用來偵測對手抽牌 */
  private prevRackCounts = new Map<string, number>();
  private currentCols = BASE_BOARD_COLS;
  private currentRows = BASE_BOARD_ROWS;

  /** 依目前已放牌的 meld 計算需要的格子尺寸。 */
  private computeNeededGrid() {
    let maxCol = 0;
    let maxRow = 0;
    this.draftBoard.forEach((meld, i) => {
      const p = this.meldPositions[i];
      if (!p) return;
      maxCol = Math.max(maxCol, p.col + meld.length);
      maxRow = Math.max(maxRow, p.row + 1);
    });
    // +1 緩衝供使用者放新組
    this.currentCols = Math.max(BASE_BOARD_COLS, Math.min(MAX_BOARD_COLS, maxCol + 1));
    this.currentRows = Math.max(BASE_BOARD_ROWS, Math.min(MAX_BOARD_ROWS, maxRow + 1));
  }
  /** 玩家手牌位置記憶：tileId → slot index（0..29，row=Math.floor(slot/10), col=slot%10）。 */
  private rackSlotMap = new Map<string, number>();
  /** 手牌中已偵測的 meld 群組（用於長按整組抓取）。tileId → 該牌所屬的整組 tile id 列表。 */
  private rackMeldByTile = new Map<string, string[]>();
  /** 長按兩階段 timer */
  private pressTimer: ReturnType<typeof setTimeout> | null = null;
  private pressTimer2: ReturnType<typeof setTimeout> | null = null;
  private pressStart: { x: number; y: number } | null = null;
  /** 群組拖曳 — 同一個 board meld 的 tiles 一起被抓起 */
  private groupDragTiles: Tile[] = [];
  private groupDragOriginMeldIdx = -1;

  constructor() {
    super('game');
  }

  create() {
    this.cameras.main.setBackgroundColor(0x1e3a8a);
    this.tableDecor = this.add.graphics().setDepth(-10);
    this.rackBg = this.add.graphics().setDepth(-5);
    this.W = this.scale.gameSize.width;
    this.H = this.scale.gameSize.height;
    this.scale.on('resize', (size: Phaser.Structs.Size) => {
      this.W = size.width;
      this.H = size.height;
      this.layoutAll(false);
    });

    const applyRemote = () => {
      const s = useStore.getState();
      const remote = s.remote;
      const myId = s.userId;
      // 偵測對手抽牌：rackCount 增加 → 動畫從牌庫飛到對方頭像
      if (remote) {
        remote.players.forEach((p, idx) => {
          const prev = this.prevRackCounts.get(p.id);
          if (prev !== undefined && p.id !== myId && p.rackCount > prev) {
            const drawn = p.rackCount - prev;
            for (let k = 0; k < drawn; k++) {
              this.scheduleOpponentDraw(idx, k * 80);
            }
          }
          this.prevRackCounts.set(p.id, p.rackCount);
        });
      }

      // 偵測新抽到的牌：上一輪 rack 沒有但本輪有的（自己）
      const beforeIds = new Set(this.draftRack.map((t) => t.id));
      this.draftBoard = s.draftBoard.map((m) => m.slice());
      this.draftRack = s.draftRack.slice();
      const newDraws = new Set<string>();
      for (const t of this.draftRack) {
        if (!beforeIds.has(t.id) && !this.prevRackIds.has(t.id)) newDraws.add(t.id);
      }
      this.prevRackIds = new Set(this.draftRack.map((t) => t.id));

      const layout = s.draftLayout.slice();
      while (layout.length < this.draftBoard.length) {
        layout.push(this.findFirstFitFor(this.draftBoard[layout.length]!.length, layout) ?? { row: 0, col: 0 });
      }
      this.meldPositions = layout.slice(0, this.draftBoard.length);
      this.layoutAll(true, newDraws);
    };
    applyRemote();
    this.storeUnsub = useStore.subscribe(applyRemote);

    this.events.on(Phaser.Scenes.Events.SHUTDOWN, () => this.storeUnsub?.());
  }

  /** 為單一新 meld 找空位（不衝突已知 layout）。 */
  private findFirstFitFor(len: number, existing: MeldPos[]): MeldPos | null {
    const used: boolean[][] = Array.from({ length: MAX_BOARD_ROWS }, () => Array(MAX_BOARD_COLS).fill(false));
    existing.forEach((p, i) => {
      const meld = this.draftBoard[i];
      if (!meld) return;
      for (let c = p.col; c < p.col + meld.length && c < MAX_BOARD_COLS; c++) {
        if (used[p.row]) used[p.row]![c] = true;
      }
    });
    for (let r = 0; r < MAX_BOARD_ROWS; r++) {
      for (let c = 0; c + len <= MAX_BOARD_COLS; c++) {
        if (this.canPlaceAt(r, c, len, used)) return { row: r, col: c };
      }
    }
    return null;
  }

  private canPlaceAt(row: number, col: number, len: number, used: boolean[][]): boolean {
    if (row < 0 || row >= MAX_BOARD_ROWS) return false;
    if (col < 0 || col + len > MAX_BOARD_COLS) return false;
    for (let c = col; c < col + len; c++) if (used[row]?.[c]) return false;
    // 左右各留 1 格隔開
    if (col > 0 && used[row]?.[col - 1]) return false;
    if (col + len < MAX_BOARD_COLS && used[row]?.[col + len]) return false;
    return true;
  }


  private get boardLayout() {
    const cellW = this.tileW + 3;
    const rowH = this.tileH + 10;
    const rowGap = 6;
    const stride = rowH + rowGap;
    const shelfContentW = this.currentCols * cellW + 20;
    const availableW = this.W - this.leftSafe - this.rightSafe;
    const shelfW = Math.min(availableW, shelfContentW);
    // 在左右安全區之間置中
    const shelfX = this.leftSafe + (availableW - shelfW) / 2;
    return { cellW, rowH, rowGap, stride, shelfW, shelfX };
  }

  private cellToXY(row: number, col: number): { x: number; y: number } {
    const L = this.boardLayout;
    const x = L.shelfX + 10 + col * L.cellW;
    const y = this.boardTop + row * L.stride + 5;
    return { x, y };
  }

  private xyToCell(px: number, py: number): { row: number; col: number } | null {
    const L = this.boardLayout;
    const innerX = px - (L.shelfX + 10);
    const innerY = py - this.boardTop - 5;
    if (innerX < 0 || innerY < 0) return null;
    const col = Math.floor(innerX / L.cellW + 0.3);
    const row = Math.floor(innerY / L.stride);
    if (col < 0 || col >= MAX_BOARD_COLS || row < 0 || row >= MAX_BOARD_ROWS) return null;
    return { row, col };
  }

  private drawTableDecor() {
    const g = this.tableDecor;
    g.clear();
    // 深藍背景（漸層暗角）
    g.fillStyle(0x1e3a8a, 1);
    g.fillRect(0, 0, this.W, this.H);
    g.fillStyle(0x1e40af, 0.5);
    g.fillEllipse(this.W / 2, this.H / 2, this.W * 1.1, this.H * 0.9);

    // 中央 Rummikub 浮水印
    if (!this.scene.get('game').data.get('watermarkRendered')) {
      // 跳過：用文字方式繪製較複雜，先不畫
    }

    // 格線：極淡的點陣（只繪製目前 currentCols × currentRows）
    const L = this.boardLayout;
    const maxY = this.getBoardMaxY();
    g.lineStyle(1, 0x60a5fa, 0.07);
    for (let r = 0; r < this.currentRows; r++) {
      const sy = this.boardTop + r * L.stride;
      if (sy + L.rowH > maxY + 10) break;
      for (let c = 0; c < this.currentCols; c++) {
        const sx = L.shelfX + 10 + c * L.cellW;
        if (sx + this.tileW > L.shelfX + L.shelfW - 6) break;
        g.strokeRoundedRect(sx, sy + 5, this.tileW, this.tileH, 4);
      }
    }

    // 牌庫位置標示（疊起來的牌背）— 抽牌動畫的視覺起點
    const dp = this.deckPos;
    const dW = this.tileW * 0.7;
    const dH = this.tileH * 0.7;
    for (let i = 2; i >= 0; i--) {
      const offX = i * 2;
      const offY = i * 2;
      g.fillStyle(0x6366f1, 0.4 - i * 0.08);
      g.fillRoundedRect(dp.x - dW / 2 + offX, dp.y - dH / 2 + offY, dW, dH, 6);
      g.lineStyle(1, 0x818cf8, 0.5);
      g.strokeRoundedRect(dp.x - dW / 2 + offX, dp.y - dH / 2 + offY, dW, dH, 6);
    }
  }

  private get boardTop() {
    // 上方留給玩家頭像 / 倒數 HUD（safe-area + status bar）
    return this.H < 600 ? 64 : 96;
  }
  /** 左側 HTML 玩家面板佔用空間 */
  private get leftSafe() {
    return this.W < 600 ? 64 : 220;
  }
  /** 右側倒數環 / 牌堆面板佔用空間 */
  private get rightSafe() {
    return this.W < 600 ? 90 : 140;
  }
  private get rackBottomYDyn() {
    // 底部留給動作列高度（手機橫向時動作列在右邊，可以更貼底）
    const isLandscapeShort = this.H < 500;
    if (isLandscapeShort) return this.H - 8;
    return this.H - 80;
  }

  /** 取得目前手牌的 y 範圍（底端對齊 rackBottomYDyn；尺寸隨 tileScale 縮放）。 */
  private getRackArea() {
    const tileH = this.tileH;
    const tileW = this.tileW;
    const rowH = tileH + RACK_GAP;
    const rackH = this.rackRows * rowH + RACK_PAD * 2 - RACK_GAP;
    const yBottom = this.rackBottomYDyn;
    const yTop = yBottom - rackH;
    const innerTopY = yTop + RACK_PAD;
    const cellW = tileW + 3;
    const innerW = RACK_COLS * cellW;
    const availableW = this.W - this.leftSafe - this.rightSafe;
    const w = Math.min(innerW + RACK_PAD * 2 + 12, availableW);
    const x = this.leftSafe + (availableW - w) / 2;
    const startX = x + (w - innerW) / 2;
    return { x, yTop, yBottom, w, h: rackH, startX, innerTopY, rowH };
  }

  /** 桌面區域的 Y 上限，依手牌行數動態調整。 */
  private getBoardMaxY(): number {
    return this.getRackArea().yTop - 16;
  }

  private drawRackBg() {
    const g = this.rackBg;
    g.clear();
    const a = this.getRackArea();
    // 木紋底（暖橘棕）
    g.fillStyle(0xc97a3c, 1);
    g.fillRoundedRect(a.x, a.yTop, a.w, a.h + 20, 16);  // 多 20px 模擬下緣立體感
    // 木紋內側陰影
    g.fillStyle(0x9a5a28, 1);
    g.fillRoundedRect(a.x + 8, a.yTop + 8, a.w - 16, a.h, 12);
    // 上緣亮帶
    g.fillStyle(0xeab574, 0.7);
    g.fillRoundedRect(a.x + 4, a.yTop + 2, a.w - 8, 10, 6);
    // 底緣陰影
    g.fillStyle(0x6b3a14, 0.6);
    g.fillRoundedRect(a.x + 6, a.yTop + a.h + 8, a.w - 12, 8, 4);
  }

  private layoutAll(animate: boolean, newDraws?: Set<string>) {
    // 先把 rackRows 算好
    this.syncRackSlots();
    let usedMaxRow = 0;
    for (const t of this.draftRack) {
      const slot = this.rackSlotMap.get(t.id);
      if (slot != null) usedMaxRow = Math.max(usedMaxRow, Math.floor(slot / RACK_COLS));
    }
    this.rackRows = Math.max(1, Math.min(RACK_MAX_ROWS, usedMaxRow + 1));

    // 計算 currentCols / currentRows（小 grid → 大 tile，反之亦然）
    this.computeNeededGrid();

    // ---- 計算 tileScale ----
    // 1. 水平：取 max(currentCols, RACK_COLS) 欄塞進「扣除左右安全區」的可用寬度
    const naturalCellW = TILE_W + 3;
    const widerCols = Math.max(this.currentCols, RACK_COLS);
    const naturalShelfW = widerCols * naturalCellW + 32;
    const usableW = this.W - this.leftSafe - this.rightSafe;
    const horizCap = Math.min(1.6, usableW / naturalShelfW);
    // 2. 垂直：需求行數必須塞進可用高度
    const maxRow = this.meldPositions.reduce((m, p) => Math.max(m, p.row), 0);
    const neededRows = maxRow + 1;
    // 先用暫定 scale 估高度（rack 用同 scale 才能準確計算可用區）
    const tempScale = horizCap;
    const tempTileH = TILE_H * tempScale;
    const tempRackH = this.rackRows * (tempTileH + RACK_GAP) + RACK_PAD * 2 - RACK_GAP;
    const tempBoardMaxY = (this.H - 8) - tempRackH - 16;
    const availableH = tempBoardMaxY - this.boardTop;
    const naturalRowH = tempTileH + 16;
    const neededH = Math.max(1, neededRows) * naturalRowH;
    // 也檢查：currentRows 全部能塞下（即使 meld 還沒占滿）
    const requiredRows = Math.max(this.currentRows, neededRows);
    const requiredH = requiredRows * naturalRowH;
    const vertCap = Math.max(0.5, Math.min(1, availableH / Math.max(requiredH, 1)));
    this.tileScale = Math.max(0.45, Math.min(horizCap, horizCap * vertCap, 1.4));
    this.tileW = Math.round(TILE_W * this.tileScale);
    this.tileH = Math.round(TILE_H * this.tileScale);
    this.drawTableDecor();

    const seen = new Set<string>();

    // 桌面：依 meldPositions
    this.draftBoard.forEach((meld, idx) => {
      const pos = this.meldPositions[idx];
      if (!pos) return;
      const v = validateMeld(meld);
      const color = v.valid ? 0x10b981 : 0xef4444;
      let border = this.meldBorders.get(idx);
      if (!border) {
        border = this.add.graphics().setDepth(-1);
        this.meldBorders.set(idx, border);
      }
      border.clear();
      const start = this.cellToXY(pos.row, pos.col);
      const w = meld.length * (this.tileW + 3) - 3;
      border.lineStyle(2, color, 0.7);
      border.strokeRoundedRect(start.x - 3, start.y - 3, w + 6, this.tileH + 6, 8);

      meld.forEach((t, i) => {
        seen.add(t.id);
        const xy = this.cellToXY(pos.row, pos.col + i);
        let s = this.tileMap.get(t.id);
        if (!s) {
          s = this.createTileSprite(t, xy.x, xy.y);
          this.tileMap.set(t.id, s);
        }
        s.__home = { x: xy.x + this.tileW / 2, y: xy.y + this.tileH / 2 };
        s.setData('src', { from: 'board', meldIdx: idx });
        s.setData('baseScale', this.tileScale);
        const tx = xy.x + this.tileW / 2;
        const ty = xy.y + this.tileH / 2;
        this.tweens.killTweensOf(s);
        if (animate) {
          // 增強彈簧感：先落到位置以下 8px → 反彈
          this.tweens.add({
            targets: s,
            x: tx,
            y: ty,
            scale: this.tileScale,
            duration: 320,
            ease: 'Back.easeOut',
            easeParams: [2.5],
          });
        } else {
          s.x = tx; s.y = ty; s.setScale(this.tileScale);
        }
      });
      // 合法 meld：邊框呼吸 glow 動畫
      if (v.valid && meld.length >= 3) {
        this.tweens.add({
          targets: border,
          alpha: { from: 0.7, to: 1 },
          duration: 1200,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    });

    for (const [k, g] of this.meldBorders) {
      if (k >= this.draftBoard.length) { g.destroy(); this.meldBorders.delete(k); }
    }

    // 牌架（使用 rackSlotMap 記憶位置；底部對齊）
    this.drawRackBg();
    const a = this.getRackArea();
    const rackCellW = this.tileW + 3;
    const rackRowH = a.rowH;
    const rackStartX = a.startX;
    const rackTopY = a.innerTopY;

    this.draftRack.forEach((t) => {
      seen.add(t.id);
      const slot = this.rackSlotMap.get(t.id) ?? 0;
      const row = Math.floor(slot / RACK_COLS);
      const col = slot % RACK_COLS;
      const ry = rackTopY + Math.min(row, RACK_MAX_ROWS - 1) * rackRowH;
      const rx = rackStartX + col * rackCellW;
      const tx = rx + this.tileW / 2;
      const ty = ry + this.tileH / 2;
      const isNewDraw = newDraws?.has(t.id);
      let s = this.tileMap.get(t.id);
      if (!s) {
        if (isNewDraw) {
          // 新抽到的牌 → 從牌庫位置飛出
          const dp = this.deckPos;
          s = this.createTileSprite(t, dp.x - this.tileW / 2, dp.y - this.tileH / 2);
          s.setScale(0.5);
          s.setAlpha(0.85);
          s.angle = -25;
          s.setDepth(800);
          playSfx('drawTile');
        } else {
          s = this.createTileSprite(t, rx, ry);
        }
        this.tileMap.set(t.id, s);
      }
      s.__home = { x: tx, y: ty };
      s.setData('src', { from: 'rack', meldIdx: -1 });
      s.setData('baseScale', this.tileScale);
      this.tweens.killTweensOf(s);
      if (animate) {
        if (isNewDraw) {
          // 從牌庫飛到 rack slot
          this.tweens.add({
            targets: s,
            x: tx,
            y: ty,
            scale: this.tileScale,
            alpha: 1,
            angle: 0,
            duration: 600,
            ease: 'Cubic.easeOut',
            onComplete: () => s!.setDepth(0),
          });
        } else {
          this.tweens.add({ targets: s, x: tx, y: ty, scale: this.tileScale, duration: 280, ease: 'Back.easeOut', easeParams: [2] });
        }
      } else {
        s.x = tx; s.y = ty; s.setScale(this.tileScale); s.setAlpha(1); s.angle = 0;
      }
    });

    for (const [id, sprite] of this.tileMap) {
      if (!seen.has(id)) {
        this.tweens.add({ targets: sprite, alpha: 0, scale: 0.5, duration: 150, onComplete: () => sprite.destroy() });
        this.tileMap.delete(id);
      } else {
        // 保險：確保每個存在的 tile 都可拖曳（避免之前狀態殘留）
        this.input.setDraggable(sprite, true);
      }
    }
  }

  private createTileSprite(tile: Tile, x: number, y: number): TileSprite {
    const key = ensureTileTexture(this, tile);
    const img = this.add.image(0, 0, key).setOrigin(0.5, 0.5);
    const container = this.add.container(x + TILE_W / 2, y + TILE_H / 2, [img]) as TileSprite;
    container.__tile = tile;
    container.setSize(TILE_W, TILE_H);
    container.setData('baseScale', 1);
    container.setInteractive({ draggable: true, useHandCursor: true });
    this.input.setDraggable(container);

    if (tile.isJoker) {
      const glow = this.add.graphics();
      glow.fillStyle(0xa855f7, 0.25);
      glow.fillCircle(0, 0, 40);
      container.addAt(glow, 0);
      this.tweens.add({ targets: glow, alpha: { from: 0.15, to: 0.45 }, duration: 1200, yoyo: true, repeat: -1 });
    }

    container.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!this.isMyTurn()) return;
      this.pressStart = { x: p.worldX, y: p.worldY };
      if (this.pressTimer) clearTimeout(this.pressTimer);
      if (this.pressTimer2) clearTimeout(this.pressTimer2);

      // 長按 0.6s → 抓整段 chain（限按住最左邊那張牌才觸發；中間 / 右邊 = 單張拖曳）
      this.pressTimer = setTimeout(() => {
        const src = container.getData('src') as { from: 'board' | 'rack'; meldIdx: number } | undefined;
        if (!src) return;
        if (src.from === 'board') {
          const meld = this.draftBoard[src.meldIdx];
          if (!meld || meld.length < 2) return;
          // 只有按住 meld 的第一張（最左）才整組抓起
          if (meld[0]?.id !== tile.id) return;
          this.setGroupSelection(meld.slice(), src.meldIdx);
          return;
        }
        // 手牌：必須按住 chain 中最左（slot 最小）那張才能整組抓起
        const full = this.findRackChain(tile, 'full');
        if (full.length < 2) return;
        const leftMost = full.reduce((acc, t) => {
          const sa = this.rackSlotMap.get(acc.id) ?? Infinity;
          const sb = this.rackSlotMap.get(t.id) ?? Infinity;
          return sb < sa ? t : acc;
        }, full[0]!);
        if (leftMost.id !== tile.id) return;
        this.setGroupSelection(full, -2);
      }, LONG_PRESS_FULL_MS);
    });

    container.on('dragstart', () => {
      if (this.pressTimer) { clearTimeout(this.pressTimer); this.pressTimer = null; }
      if (this.pressTimer2) { clearTimeout(this.pressTimer2); this.pressTimer2 = null; }
      this.tweens.killTweensOf(container);
      container.setDepth(1000);
      const baseScale = container.getData('baseScale') ?? 1;
      // 上升 + 放大 + 微旋（更明顯的拾起感）
      this.tweens.add({ targets: container, scale: baseScale * 1.18, duration: 140, ease: 'Back.easeOut' });
      this.tweens.add({ targets: container, angle: { from: -3, to: 3 }, duration: 80, yoyo: true });
      // 投影：在 tile 下方加深色橢圓陰影
      if (!container.__shadow) {
        const sh = this.add.graphics();
        sh.fillStyle(0x000000, 0.35);
        sh.fillEllipse(0, 0, this.tileW * 0.9, this.tileH * 0.18);
        const shTex = this.add.renderTexture(0, 0, this.tileW, this.tileH * 0.3).setVisible(false);
        shTex.draw(sh, 0, 0);
        sh.destroy();
        const shImg = this.add.image(container.x, container.y + this.tileH * 0.5, '').setVisible(true).setAlpha(0);
        // 用 graphic 直接畫陰影
        container.__shadow = shImg;
        shTex.destroy();
      }
      // 簡化：用內建 graphic 動態畫陰影
      const shadow = this.add.graphics().setDepth(999);
      shadow.fillStyle(0x000000, 0.4);
      shadow.fillEllipse(0, 0, this.tileW * 0.85, this.tileH * 0.2);
      shadow.x = container.x;
      shadow.y = container.y + this.tileH * 0.55;
      container.setData('shadowGfx', shadow);
      this.tweens.add({ targets: shadow, alpha: { from: 0, to: 1 }, duration: 100 });
      this.input.manager.canvas.style.cursor = 'grabbing';
      if ('vibrate' in navigator) navigator.vibrate?.(10);
      playSfx('tilePick');
    });

    container.on('drag', (p: Phaser.Input.Pointer, dx: number, dy: number) => {
      if (this.groupDragTiles.length > 0 && this.groupDragTiles.some((t) => t.id === tile.id)) {
        const offsetX = dx - (container.__home?.x ?? container.x) + (this.tileW / 2);
        const offsetY = dy - (container.__home?.y ?? container.y) + (this.tileH / 2);
        for (const t of this.groupDragTiles) {
          const s = this.tileMap.get(t.id);
          if (s && s.__home) {
            s.x = s.__home.x + offsetX;
            s.y = s.__home.y + offsetY;
          }
        }
        this.updateGhostGroup(p.worldX, p.worldY);
        return;
      }
      container.x = dx;
      container.y = dy;
      // 陰影跟隨（保持垂直距離）
      const shadow = container.getData('shadowGfx') as Phaser.GameObjects.Graphics | undefined;
      if (shadow) {
        shadow.x = dx;
        shadow.y = dy + this.tileH * 0.55;
      }
      this.updateGhost(p.worldX, p.worldY, tile);
    });

    container.on('dragend', (p: Phaser.Input.Pointer) => {
      this.input.manager.canvas.style.cursor = '';
      this.clearGhost();
      if (this.pressTimer) { clearTimeout(this.pressTimer); this.pressTimer = null; }
      if (this.pressTimer2) { clearTimeout(this.pressTimer2); this.pressTimer2 = null; }
      this.tweens.killTweensOf(container);
      const baseScale = container.getData('baseScale') ?? 1;
      container.setScale(baseScale);
      container.angle = 0;
      container.setDepth(0);
      // 移除陰影
      const shadow = container.getData('shadowGfx') as Phaser.GameObjects.Graphics | undefined;
      if (shadow) {
        this.tweens.add({ targets: shadow, alpha: 0, duration: 120, onComplete: () => shadow.destroy() });
        container.setData('shadowGfx', null);
      }
      if (!this.isMyTurn()) {
        // 非我的回合：仍允許「整理手牌」（rack-internal 位置調整）
        this.groupDragTiles = [];
        const src = container.getData('src') as { from: 'board' | 'rack' } | undefined;
        if (src?.from === 'rack') {
          const targetSlot = this.xyToRackSlot(p.worldX, p.worldY);
          if (targetSlot != null) {
            this.assignRackSlot(tile, targetSlot, this.draftRack.filter((t) => t.id !== tile.id));
          }
        }
        this.layoutAll(true);
        return;
      }
      if (this.groupDragTiles.length > 0 && this.groupDragTiles.some((t) => t.id === tile.id)) {
        this.applyGroupDrop(p.worldX, p.worldY);
        this.groupDragTiles = [];
        this.groupDragOriginMeldIdx = -1;
        return;
      }
      this.applyDrop(tile, p.worldX, p.worldY);
    });

    container.on('pointerup', () => {
      if (this.pressTimer) { clearTimeout(this.pressTimer); this.pressTimer = null; }
      if (this.pressTimer2) { clearTimeout(this.pressTimer2); this.pressTimer2 = null; }
    });

    return container;
  }

  private isMyTurn() {
    const s = useStore.getState();
    return s.remote?.currentTurnPlayerId === s.userId && s.remote?.status === 'PLAYING';
  }

  private updateGhost(px: number, py: number, tile: Tile) {
    const cell = this.xyToCell(px, py);
    if (!cell) {
      this.clearGhost();
      this.hoverCell = null;
      return;
    }
    this.hoverCell = cell;
    const xy = this.cellToXY(cell.row, cell.col);
    const key = ensureTileTexture(this, tile);
    const sim = this.simulatePlace(tile, cell.row, cell.col);
    // 綠 = 合法 / 紅 = 會被拒絕 / 白 = 暫擺（不完整、合併後仍有效）
    const tint = sim.valid ? 0x10b981 : sim.willReject ? 0xef4444 : 0xffffff;
    const alpha = sim.willReject ? 0.3 : 0.55;
    if (!this.ghostPreview) {
      this.ghostPreview = this.add.image(0, 0, key).setOrigin(0, 0).setDepth(500);
    }
    this.ghostPreview.setTexture(key).setVisible(true).setAlpha(alpha).setTint(tint).setPosition(xy.x, xy.y).setScale(this.tileScale);
  }

  private clearGhost() {
    if (this.ghostPreview) this.ghostPreview.setVisible(false);
  }

  private updateGhostGroup(px: number, py: number) {
    // 群組 ghost：暫不顯示獨立 ghost，由群組 sprites 自身顯示位置即可
    this.clearGhost();
    void px; void py;
  }

  /** 把整組 meld 從原位置拔起，移到 (drop row, col)。要求新位置足夠寬度且不重疊。 */
  private applyGroupDrop(px: number, py: number) {
    const meldIdx = this.groupDragOriginMeldIdx;
    const cell = this.xyToCell(px, py);
    const tiles = this.groupDragTiles;
    if (tiles.length === 0) { this.layoutAll(true); return; }

    if (meldIdx === -2) {
      // 來源為手牌群組 → 嘗試整組移到桌面
      if (!cell) { this.layoutAll(true); return; }
      const fits = (row: number, col: number, len: number) => {
        if (col < 0 || col + len > 14) return false;
        for (let i = 0; i < this.draftBoard.length; i++) {
          const p = this.meldPositions[i]!;
          const m = this.draftBoard[i]!;
          if (p.row !== row) continue;
          const aL = col - 1, aR = col + len;
          const bL = p.col, bR = p.col + m.length - 1;
          if (!(aR < bL || aL > bR)) return false;
        }
        return true;
      };
      if (!fits(cell.row, cell.col, tiles.length)) { this.flashIllegal(); this.layoutAll(true); return; }
      const tileIds = new Set(tiles.map((t) => t.id));
      const newRack = this.draftRack.filter((t) => !tileIds.has(t.id));
      const newBoard = [...this.draftBoard, tiles.slice()];
      const newPositions = [...this.meldPositions, { row: cell.row, col: cell.col }];
      this.meldPositions = newPositions;
      useStore.getState().setDraft(newBoard, newRack, newPositions);
      send('moveTile', { board: newBoard, rack: newRack, layout: newPositions });
      playSfx('tilePlace');
      return;
    }

    // 來源為桌面 meld
    if (meldIdx < 0) return;
    const meld = this.draftBoard[meldIdx];
    if (!meld) return;
    if (!cell) { this.layoutAll(true); return; }

    const others = this.meldPositions.filter((_, i) => i !== meldIdx);
    const otherMelds = this.draftBoard.filter((_, i) => i !== meldIdx);
    const fits = (row: number, col: number, len: number) => {
      if (col < 0 || col + len > 14) return false;
      for (let i = 0; i < others.length; i++) {
        const p = others[i]!;
        const m = otherMelds[i]!;
        if (p.row !== row) continue;
        const aL = col - 1, aR = col + len;
        const bL = p.col, bR = p.col + m.length - 1;
        if (!(aR < bL || aL > bR)) return false;
      }
      return true;
    };
    if (!fits(cell.row, cell.col, meld.length)) {
      this.flashIllegal();
      this.layoutAll(true);
      return;
    }
    const newPositions = this.meldPositions.slice();
    newPositions[meldIdx] = { row: cell.row, col: cell.col };
    this.meldPositions = newPositions;
    useStore.getState().setDraft(this.draftBoard, this.draftRack, newPositions);
    send('moveTile', { board: this.draftBoard, rack: this.draftRack, layout: newPositions });
  }

  /** 模擬把 tile 放到 (row, col)：判斷是否佔用、放置後新 meld 是否合法。 */
  private simulatePlace(tile: Tile, row: number, col: number): { valid: boolean; occupied: boolean; willReject: boolean } {
    const occupant = this.findTileAtCell(row, col, tile.id);
    if (occupant) return { valid: false, occupied: true, willReject: true };
    const { mergedMeld, mergeIndices } = this.simulateMerge(tile, row, col);
    const wouldMerge = mergeIndices.length > 0;
    const v = validateMeld(mergedMeld);
    // 若合併後 ≥3 張且不合法 → 會被拒絕
    const willReject = wouldMerge && mergedMeld.length >= 3 && !v.valid;
    return { valid: v.valid, occupied: false, willReject };
  }

  private findTileAtCell(row: number, col: number, excludeId: string): Tile | null {
    for (let i = 0; i < this.draftBoard.length; i++) {
      const pos = this.meldPositions[i];
      const meld = this.draftBoard[i]!;
      if (!pos || pos.row !== row) continue;
      const offset = col - pos.col;
      if (offset >= 0 && offset < meld.length) {
        const t = meld[offset]!;
        if (t.id !== excludeId) return t;
      }
    }
    return null;
  }

  /** 模擬把 tile 放到 (row, col)，若相鄰則自動 merge 成一個 meld。 */
  private simulateMerge(tile: Tile, row: number, col: number): {
    mergedMeld: Meld;
    mergeIndices: number[];   // 要被吞掉的 meld 索引
    startCol: number;
  } {
    // 找同一行中「末端 = col-1」或「起始 = col+1」的 meld
    const leftIdx = this.draftBoard.findIndex((m, i) => {
      const p = this.meldPositions[i];
      return p && p.row === row && p.col + m.length === col;
    });
    const rightIdx = this.draftBoard.findIndex((m, i) => {
      const p = this.meldPositions[i];
      return p && p.row === row && p.col === col + 1;
    });

    const parts: Tile[] = [];
    let startCol = col;
    const mergeIndices: number[] = [];
    if (leftIdx >= 0) {
      const leftMeld = this.draftBoard[leftIdx]!;
      parts.push(...leftMeld);
      startCol = this.meldPositions[leftIdx]!.col;
      mergeIndices.push(leftIdx);
    }
    parts.push(tile);
    if (rightIdx >= 0) {
      const rightMeld = this.draftBoard[rightIdx]!;
      parts.push(...rightMeld);
      mergeIndices.push(rightIdx);
    }
    return { mergedMeld: parts, mergeIndices, startCol };
  }

  private applyDrop(tile: Tile, px: number, py: number) {
    // 規則檢查：場上的牌不可放回手牌（無論本回合或之前打的）
    const fromBoard = this.draftBoard.some((m) => m.some((t) => t.id === tile.id));
    const rackZoneCheck = py >= this.getRackArea().yTop && py <= this.H;
    if (fromBoard && rackZoneCheck) {
      // 進一步：若是「本回合自己剛放上去的牌」可以收回；用 turnInitialRackIds 區分
      const initialIds = useStore.getState().turnInitialRackIds;
      if (!initialIds.has(tile.id)) {
        // 該牌一開始就在桌面，不可放回手牌
        this.flashIllegal();
        this.showRejectToast('場上的牌不可放回手牌');
        this.layoutAll(true);
        return;
      }
    }

    // 先從當前 draftBoard / draftRack 移除 tile
    const [b0, r0, oldMeldIdx] = this.extractTile(tile);
    let newBoard = b0;
    let newRack = r0;
    let newPositions = this.meldPositions.slice();
    // 若移除後某 meld 為空，position 也要移除
    if (oldMeldIdx >= 0 && !newBoard[oldMeldIdx]) {
      newPositions.splice(oldMeldIdx, 1);
    }

    const cell = this.xyToCell(px, py);
    const dropOnBoard = cell !== null && py < this.getBoardMaxY() + 20;

    if (!dropOnBoard) {
      newRack = [...r0, tile];
      // 計算 drop 位置對應的 rack slot；若該 slot 已被佔，找最近空 slot
      const targetSlot = this.xyToRackSlot(px, py);
      this.assignRackSlot(tile, targetSlot, r0);
      this.meldPositions = newPositions;
      useStore.getState().setDraft(newBoard, newRack, newPositions);
      send('moveTile', { board: newBoard, rack: newRack, layout: newPositions });
      return;
    }

    // ---- 智慧合併：先試「就近吸附」（容忍 ±2 格、含落在 meld 內） ----
    // 收集所有候選：同 row 中可延伸 / 合併的 meld，依距離排序
    type Cand = { meldIdx: number; side: 'left' | 'right'; dist: number; parts: Tile[]; startCol: number };
    const candidates: Cand[] = [];
    for (let i = 0; i < newBoard.length; i++) {
      const m = newBoard[i]!;
      const p = newPositions[i]!;
      if (p.row !== cell.row) continue;
      const leftAnchor = p.col - 1;
      const rightAnchor = p.col + m.length;
      // 距離以「meld 主體最近一端」為基準（落在 meld 上時 dist=0；右延伸位距=右端距離）
      const meldStart = p.col;
      const meldEnd = p.col + m.length - 1;
      const insideMeld = cell.col >= meldStart && cell.col <= meldEnd;
      const distLeft = insideMeld ? cell.col - meldStart + 1 : Math.abs(cell.col - leftAnchor);
      const distRight = insideMeld ? meldEnd - cell.col + 1 : Math.abs(cell.col - rightAnchor);
      // 右延伸
      if (distRight <= 3) {
        const trial = [...m, tile];
        if ((trial.length < 3 ? this.canPotentiallyExtend(trial) : validateMeld(trial).valid)) {
          candidates.push({ meldIdx: i, side: 'right', dist: distRight, parts: trial, startCol: p.col });
        }
      }
      // 左延伸
      if (distLeft <= 3) {
        const trial = [tile, ...m];
        if ((trial.length < 3 ? this.canPotentiallyExtend(trial) : validateMeld(trial).valid)) {
          candidates.push({ meldIdx: i, side: 'left', dist: distLeft, parts: trial, startCol: p.col - 1 });
        }
      }
    }
    // ---- 自動一刀切開：插入後若單組非法，嘗試把 meld 切成兩個合法的 meld ----
    type SplitCand = { meldIdx: number; left: Tile[]; right: Tile[]; dist: number };
    const splitCandidates: SplitCand[] = [];
    for (let i = 0; i < newBoard.length; i++) {
      const m = newBoard[i]!;
      const p = newPositions[i]!;
      if (p.row !== cell.row) continue;
      // 落點要在或附近 meld 範圍
      const meldStart = p.col;
      const meldEnd = p.col + m.length - 1;
      if (cell.col < meldStart - 1 || cell.col > meldEnd + 1) continue;
      // 試所有切點 K（左邊長度），新 tile 加左或加右
      for (let K = 1; K < m.length; K++) {
        const left = m.slice(0, K);
        const right = m.slice(K);
        // 新 tile 加到左半末端
        const leftWithNew = [...left, tile];
        if (leftWithNew.length >= 3 && right.length >= 3 && validateMeld(leftWithNew).valid && validateMeld(right).valid) {
          // 切點落點距離（越接近落點越優）
          const splitCol = p.col + K;
          const dist = Math.abs(cell.col - splitCol);
          splitCandidates.push({ meldIdx: i, left: leftWithNew, right, dist });
        }
        // 新 tile 加到右半開頭
        const rightWithNew = [tile, ...right];
        if (left.length >= 3 && rightWithNew.length >= 3 && validateMeld(left).valid && validateMeld(rightWithNew).valid) {
          const splitCol = p.col + K;
          const dist = Math.abs(cell.col - splitCol);
          splitCandidates.push({ meldIdx: i, left, right: rightWithNew, dist });
        }
      }
    }
    splitCandidates.sort((a, b) => a.dist - b.dist);

    candidates.sort((a, b) => a.dist - b.dist);

    // 若有合法的單側延伸 → 優先用（保留原 meld 結構）
    // 否則嘗試切割
    if (candidates.length === 0 && splitCandidates.length > 0) {
      const best = splitCandidates[0]!;
      const origPos = newPositions[best.meldIdx]!;
      const leftLen = best.left.length;
      const rightLen = best.right.length;

      // 右半新位置 = 原 col + leftLen + 1（留 1 格隔離）
      const rightCol = origPos.col + leftLen + 1;
      const rightEndCol = rightCol + rightLen - 1;
      if (rightEndCol >= 14) { /* 超出格線，無法切 */ }
      else {
        // 檢查右半新位置是否與其他 meld 衝突（同 row 之後若有 meld，需位移）
        // 簡化：直接檢查衝突，有衝突就不切
        const conflict = newBoard.some((m, i) => {
          if (i === best.meldIdx) return false;
          const p = newPositions[i]!;
          if (p.row !== origPos.row) return false;
          const aL = rightCol - 1, aR = rightEndCol + 1;
          const bL = p.col, bR = p.col + m.length - 1;
          return !(aR < bL || aL > bR);
        });
        if (!conflict) {
          // 套用切割
          const replacedBoard = newBoard.slice();
          const replacedPos = newPositions.slice();
          replacedBoard[best.meldIdx] = best.left;
          replacedPos[best.meldIdx] = { row: origPos.row, col: origPos.col };
          replacedBoard.push(best.right);
          replacedPos.push({ row: origPos.row, col: rightCol });
          this.meldPositions = replacedPos;
          useStore.getState().setDraft(replacedBoard, newRack, replacedPos);
          send('moveTile', { board: replacedBoard, rack: newRack, layout: replacedPos });
          playSfx('jokerSwap'); // 用魔法音表示「神奇切割」
          return;
        }
      }
    }

    if (candidates.length > 0) {
      // 取最近的合法延伸
      const best = candidates[0]!;
      // 還要把另一側也合併進來（如果剛好填補空隙連接兩組）
      const tail = newBoard[best.meldIdx]!;
      const tailPos = newPositions[best.meldIdx]!;
      let parts = best.parts;
      let startCol = best.startCol;
      const toRemove = [best.meldIdx];

      const otherSide = best.side === 'right'
        ? newBoard.findIndex((m2, i) =>
            i !== best.meldIdx && newPositions[i]!.row === cell.row &&
            newPositions[i]!.col === tailPos.col + tail.length + 1)
        : newBoard.findIndex((m2, i) =>
            i !== best.meldIdx && newPositions[i]!.row === cell.row &&
            newPositions[i]!.col + (m2.length) === tailPos.col - 1);
      if (otherSide >= 0) {
        const otherMeld = newBoard[otherSide]!;
        const otherPos = newPositions[otherSide]!;
        if (best.side === 'right') {
          parts = [...parts, ...otherMeld];
        } else {
          parts = [...otherMeld, ...parts];
          startCol = otherPos.col;
        }
        if (parts.length >= 3 && !validateMeld(parts).valid) {
          // 加上另一組後反而非法 → 不合併，回退到只接單側
          parts = best.parts;
          startCol = best.startCol;
        } else {
          toRemove.push(otherSide);
        }
      }

      toRemove.sort((a, b) => b - a);
      for (const i of toRemove) { newBoard.splice(i, 1); newPositions.splice(i, 1); }
      newBoard.push(parts);
      newPositions.push({ row: cell.row, col: startCol });
      this.meldPositions = newPositions;
      useStore.getState().setDraft(newBoard, newRack, newPositions);
      send('moveTile', { board: newBoard, rack: newRack, layout: newPositions });
      playSfx('tilePlace');
      return;
    }

    // 沒有可合併的鄰居 → 檢查是否落在被佔用以外的同 row 鄰居 ±2 範圍但延伸不合法
    const hasNearbyIllegal = newBoard.some((m, i) => {
      const p = newPositions[i]!;
      if (p.row !== cell.row) return false;
      return Math.abs(cell.col - (p.col - 1)) <= 2 || Math.abs(cell.col - (p.col + m.length)) <= 2;
    });
    if (hasNearbyIllegal) {
      // 鄰近有 meld 但延伸後非法 → 退回牌架
      newRack = [...r0, tile];
      this.meldPositions = newPositions;
      useStore.getState().setDraft(newBoard, newRack, newPositions);
      this.flashIllegal();
      this.showRejectToast('無法組合');
      send('moveTile', { board: newBoard, rack: newRack, layout: newPositions });
      return;
    }

    // 完全獨立位置 → 建立新組
    newBoard.push([tile]);
    newPositions.push({ row: cell.row, col: cell.col });

    this.meldPositions = newPositions;
    useStore.getState().setDraft(newBoard, newRack, newPositions);
    send('moveTile', { board: newBoard, rack: newRack, layout: newPositions });
  }

  private extractTile(tile: Tile): [Meld[], Tile[], number] {
    const newBoard: Meld[] = [];
    let foundMeldIdx = -1;
    for (let i = 0; i < this.draftBoard.length; i++) {
      const m = this.draftBoard[i]!;
      const idx = m.findIndex((t) => t.id === tile.id);
      if (idx >= 0) {
        foundMeldIdx = i;
        const nm = m.slice();
        nm.splice(idx, 1);
        // 若拆成兩段，分別建成兩個 meld
        if (idx > 0 && idx < m.length - 1) {
          // 拆成左右兩段
          const left = m.slice(0, idx);
          const right = m.slice(idx + 1);
          newBoard.push(left);
          newBoard.push(right);
        } else if (nm.length > 0) {
          newBoard.push(nm);
        }
        // 若 nm.length === 0，不 push（整組消失）
      } else {
        newBoard.push(m.slice());
      }
    }
    const newRack = foundMeldIdx >= 0 ? this.draftRack.slice() : this.draftRack.filter((t) => t.id !== tile.id);
    return [newBoard, newRack, foundMeldIdx];
  }

  /** 將目前 draftRack 同步到 rackSlotMap：保留既有位置，新增 tile 找最小空 slot。 */
  private syncRackSlots() {
    const inRack = new Set(this.draftRack.map((t) => t.id));
    // 移除不在 rack 的 mapping
    for (const k of this.rackSlotMap.keys()) {
      if (!inRack.has(k)) this.rackSlotMap.delete(k);
    }
    // 找已使用的 slot
    const used = new Set<number>(this.rackSlotMap.values());
    // 為新 tile 分配 slot（依目前 rack 順序，找最小空 slot）
    for (const t of this.draftRack) {
      if (this.rackSlotMap.has(t.id)) continue;
      for (let i = 0; i < RACK_TOTAL_SLOTS; i++) {
        if (!used.has(i)) { this.rackSlotMap.set(t.id, i); used.add(i); break; }
      }
    }
  }

  /** 依排序按鈕重排手牌位置（依目前 draftRack 順序填入 slot 0..n-1）。 */
  applySortToRackLayout() {
    this.rackSlotMap.clear();
    this.draftRack.forEach((t, i) => this.rackSlotMap.set(t.id, i));
    this.rackMeldByTile.clear();
    this.layoutAll(true);
  }

  /** 兩張 tile 是否可組成 partial run（同色相連）或 partial group（同數不同色）。 */
  private isCompatibleChain(tiles: Tile[]): boolean {
    if (tiles.length < 2) return true;
    const c0 = tiles[0]!.color;
    // Run: 同色 + 連號
    if (c0 && tiles.every((t) => !t.isJoker && t.color === c0)) {
      const nums = tiles.map((t) => t.number).filter((n): n is number => n != null).sort((a, b) => a - b);
      for (let i = 1; i < nums.length; i++) if (nums[i] !== nums[i - 1]! + 1) return false;
      return nums.length === tiles.length;
    }
    // Group: 同數 + 不同色
    const n0 = tiles[0]!.number;
    if (n0 != null && tiles.every((t) => !t.isJoker && t.number === n0)) {
      const colors = new Set(tiles.map((t) => t.color).filter((c) => !!c));
      return colors.size === tiles.length;
    }
    return false;
  }

  /** 找手牌中以 tile 為起點、向左右擴張的相容 chain（在同 slot row 中相鄰）。 */
  private findRackChain(tile: Tile, mode: 'pair' | 'full'): Tile[] {
    const slot = this.rackSlotMap.get(tile.id);
    if (slot == null) return [tile];
    const row = Math.floor(slot / RACK_COLS);
    // 取出同 row 的所有 tile + slot
    const rowItems = this.draftRack
      .map((t) => ({ t, s: this.rackSlotMap.get(t.id) ?? -1 }))
      .filter((x) => x.s >= 0 && Math.floor(x.s / RACK_COLS) === row)
      .sort((a, b) => a.s - b.s);

    const idx = rowItems.findIndex((x) => x.t.id === tile.id);
    if (idx < 0) return [tile];

    const chain: Tile[] = [tile];
    // 向右擴張
    for (let i = idx + 1; i < rowItems.length; i++) {
      // slot 必須緊鄰
      if (rowItems[i]!.s !== rowItems[i - 1]!.s + 1) break;
      const trial = [...chain, rowItems[i]!.t];
      if (!this.isCompatibleChain(trial)) break;
      chain.push(rowItems[i]!.t);
      if (mode === 'pair' && chain.length >= 2) return chain;
    }
    // 向左擴張
    for (let i = idx - 1; i >= 0; i--) {
      if (rowItems[i + 1]!.s !== rowItems[i]!.s + 1) break;
      const trial = [rowItems[i]!.t, ...chain];
      if (!this.isCompatibleChain(trial)) break;
      chain.unshift(rowItems[i]!.t);
      if (mode === 'pair' && chain.length >= 2) return chain;
    }
    return chain;
  }

  private setGroupSelection(tiles: Tile[], originIdx: number) {
    // 還原前一次選擇的 tile depth
    for (const old of this.groupDragTiles) {
      if (tiles.some((t) => t.id === old.id)) continue;
      const s = this.tileMap.get(old.id);
      if (s) {
        this.tweens.killTweensOf(s);
        const baseScale = s.getData('baseScale') ?? 1;
        if (s.__home) this.tweens.add({ targets: s, scale: baseScale, y: s.__home.y, duration: 100 });
        s.setDepth(0);
      }
    }
    this.groupDragTiles = tiles;
    this.groupDragOriginMeldIdx = originIdx;
    for (const t of tiles) {
      const s = this.tileMap.get(t.id);
      if (s) {
        this.tweens.killTweensOf(s);
        const baseScale = s.getData('baseScale') ?? 1;
        if (s.__home) this.tweens.add({ targets: s, scale: baseScale * 1.1, y: s.__home.y - 8, duration: 120 });
        s.setDepth(900);
      }
    }
    if ('vibrate' in navigator) navigator.vibrate?.(15);
    playSfx('tileGroupPick');
  }

  /** 套用智慧排序的 slot map 與 meld 偵測。 */
  applySortLayoutWithGaps(slotMap: Map<string, number>, melds: Tile[][]) {
    this.rackSlotMap = new Map(slotMap);
    this.rackMeldByTile.clear();
    for (const m of melds) {
      const ids = m.map((t) => t.id);
      for (const t of m) this.rackMeldByTile.set(t.id, ids);
    }
    this.layoutAll(true);
  }

  /** 指派 tile 到指定 rack slot。slot 已被佔用 → 找鄰近空 slot；找不到空 → 用最後一格。 */
  private assignRackSlot(tile: Tile, preferred: number | null, otherTilesInRack: Tile[]) {
    const used = new Set<number>();
    for (const t of otherTilesInRack) {
      const s = this.rackSlotMap.get(t.id);
      if (s != null) used.add(s);
    }
    if (preferred != null && !used.has(preferred) && preferred >= 0 && preferred < RACK_TOTAL_SLOTS) {
      this.rackSlotMap.set(tile.id, preferred);
      return;
    }
    // 找最近空 slot
    if (preferred != null) {
      for (let d = 1; d < RACK_TOTAL_SLOTS; d++) {
        for (const candidate of [preferred - d, preferred + d]) {
          if (candidate >= 0 && candidate < RACK_TOTAL_SLOTS && !used.has(candidate)) {
            this.rackSlotMap.set(tile.id, candidate);
            return;
          }
        }
      }
    }
    // fallback：第一個空 slot
    for (let i = 0; i < RACK_TOTAL_SLOTS; i++) {
      if (!used.has(i)) { this.rackSlotMap.set(tile.id, i); return; }
    }
  }

  /** 將座標轉為 rack 中的 slot index；若超出 rack 區域回 null。 */
  private xyToRackSlot(px: number, py: number): number | null {
    const a = this.getRackArea();
    const innerX = px - a.startX;
    const innerY = py - a.innerTopY;
    if (innerY < -10) return null;
    const rackCellW = this.tileW + 3;
    const col = Math.floor(innerX / rackCellW + 0.3);
    const row = Math.max(0, Math.min(RACK_MAX_ROWS - 1, Math.floor(innerY / a.rowH)));
    if (col < 0 || col >= RACK_COLS) return null;
    return row * RACK_COLS + col;
  }

  /** 兩張 (或更多) 牌是否還有可能延伸成合法 meld？阻擋同色同號等死路。 */
  private canPotentiallyExtend(meld: Tile[]): boolean {
    if (meld.length < 2) return true;
    const nonJokers = meld.filter((t) => !t.isJoker);
    // 全是 joker → 還可以擴張為任何路線
    if (nonJokers.length === 0) return true;
    // 只有 1 張非 joker → 永遠合法（任何路線都可能）
    if (nonJokers.length === 1) return true;
    // 檢查同色同號重複（死路）
    const seen = new Set<string>();
    for (const t of nonJokers) {
      const key = `${t.color}-${t.number}`;
      if (seen.has(key)) return false;
      seen.add(key);
    }
    const colors = new Set(nonJokers.map((t) => t.color));
    const numbers = new Set(nonJokers.map((t) => t.number));
    // Run 路線
    if (colors.size === 1) {
      const nums = [...numbers].filter((n): n is number => n != null).sort((a, b) => a - b);
      if (nums[nums.length - 1]! - nums[0]! > 12) return false;
      return true;
    }
    // Group 路線
    if (numbers.size === 1) {
      return colors.size === nonJokers.length;
    }
    return false;
  }

  /** 對手抽牌動畫：從牌庫飛出一張紫色牌背 → 對方頭像位置 */
  private scheduleOpponentDraw(playerIdx: number, delay: number) {
    setTimeout(() => {
      const dp = this.deckPos;
      // 對手頭像在 HTML 左上方（player-panel）。映射到 Phaser canvas 座標。
      const isMobile = this.W < 600;
      const avatarSize = isMobile ? 40 : 56;
      const padTop = isMobile ? 8 : 12;
      const padLeft = isMobile ? 8 : 12;
      const rowH = isMobile ? 6 : 12;
      const ax = padLeft + avatarSize / 2;
      const ay = padTop + (avatarSize + rowH) * playerIdx + avatarSize / 2;

      const back = this.add.graphics();
      const w = this.tileW * 0.7;
      const h = this.tileH * 0.7;
      back.fillStyle(0x6366f1, 1);
      back.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
      back.lineStyle(2, 0xc7d2fe, 0.85);
      back.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
      // 牌背中央小符號
      back.fillStyle(0xa5b4fc, 0.9);
      back.fillCircle(0, 0, 6);
      back.x = dp.x;
      back.y = dp.y;
      back.setDepth(1500);
      this.tweens.add({
        targets: back,
        x: ax,
        y: ay,
        scaleX: 0.4,
        scaleY: 0.4,
        alpha: 0,
        rotation: 0.6,
        duration: 550,
        ease: 'Cubic.easeIn',
        onComplete: () => back.destroy(),
      });
      playSfx('drawTile');
    }, delay);
  }

  private flashIllegal() {
    if ('vibrate' in navigator) navigator.vibrate?.([30, 30, 30]);
    playSfx('tileInvalid');
    const flash = this.add.rectangle(this.W / 2, this.H / 2, this.W, this.H, 0xef4444, 0.15).setDepth(9999);
    this.tweens.add({ targets: flash, alpha: 0, duration: 400, onComplete: () => flash.destroy() });
  }

  private showRejectToast(text: string) {
    const t = this.add.text(this.W / 2, 200, text, {
      fontFamily: '"Inter", "Noto Sans TC", Arial',
      fontSize: '32px',
      fontStyle: 'bold',
      color: '#ffffff',
      backgroundColor: '#dc2626',
      padding: { x: 24, y: 12 },
    }).setOrigin(0.5).setDepth(10000).setAlpha(0);
    this.tweens.add({
      targets: t,
      alpha: { from: 0, to: 1 },
      y: { from: 180, to: 220 },
      duration: 200,
      ease: 'Back.easeOut',
      yoyo: true,
      hold: 500,
      onComplete: () => t.destroy(),
    });
  }
}
